import { Link } from "react-router-dom";
import { Button, Tabs, Table, Tag, Descriptions, Space, Progress } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import moment from "moment";

import { useCanEdit } from "@/hooks/usePermission";
import { useBondDetail } from "./useBondDetail";
import { useBondHoldersExcel } from "./useBondHoldersExcel";

const STATUS_LABELS: Record<string, { text: string; color: string }> = {
  pending: { text: "Хүлээгдэж буй", color: "default" },
  processing: { text: "Явагдаж байна", color: "processing" },
  completed: { text: "Дууссан", color: "success" },
  failed: { text: "Алдаатай", color: "error" },
};

export const BondDetailWidget = ({ _id }: { _id: string }) => {
  const {
    bondData,
    holdersData,
    isHoldersLoading,
    setHoldersPage,
    payoutsData,
    isPayoutsLoading,
    setPayoutsPage,
    withdrawalsData,
    isWithdrawalsLoading,
    setWithdrawalsPage,
    batchesData,
    isBatchesLoading,
    limit,
  } = useBondDetail(_id);

  const canEdit = useCanEdit("bonds");
  const { generateExcel, loading: excelLoading } = useBondHoldersExcel(_id);

  return (
    <>
      <div className="page-header">
        <h3>{bondData?.name ?? "Бонд дэлгэрэнгүй"}</h3>
        <div className="page-header-buttons">
          <Space>
            {canEdit && (
              <Link to={`/bonds/edit/${_id}`}>
                <Button icon={<EditOutlined />} type="primary">
                  Засах
                </Button>
              </Link>
            )}
            <Link to="/bonds">
              <Button icon={<ArrowLeftOutlined />} className="back-button">
                Буцах
              </Button>
            </Link>
          </Space>
        </div>
      </div>
      <div className="page-body">
        <Tabs
          items={[
            {
              key: "info",
              label: "Мэдээлэл",
              children: (
                <Descriptions bordered column={2}>
                  <Descriptions.Item label="Нэр">
                    {bondData?.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Symbol">
                    <Tag>{bondData?.symbol}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Хүү">
                    {bondData?.interestRate}%
                  </Descriptions.Item>
                  <Descriptions.Item label="Хугацаа">
                    {bondData?.duration}
                  </Descriptions.Item>
                  <Descriptions.Item label="Төрөл">
                    {bondData?.paymentType}
                  </Descriptions.Item>
                  <Descriptions.Item label="Төлөв">
                    <Tag color={bondData?.active ? "green" : "red"}>
                      {bondData?.active ? "Идэвхтэй" : "Идэвхгүй"}
                    </Tag>
                  </Descriptions.Item>
                  {bondData?.endDate && (
                    <Descriptions.Item label="Дуусах хугацаа">
                      {moment(bondData.endDate).format("YYYY-MM-DD")}
                    </Descriptions.Item>
                  )}
                  {bondData?.paymentSchedules &&
                    bondData.paymentSchedules.length > 0 && (
                      <Descriptions.Item label="Төлөлтийн хуваарь">
                        {bondData.paymentSchedules.map((s: string | null) => (
                          <Tag key={s}>{s}</Tag>
                        ))}
                      </Descriptions.Item>
                    )}
                </Descriptions>
              ),
            },
            {
              key: "holders",
              label: "Худалдан авалтууд",
              children: (
                <>
                  <div
                    style={{
                      marginBottom: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                    }}
                  >
                    <Button
                      icon={<FileExcelOutlined />}
                      onClick={generateExcel}
                      loading={excelLoading}
                    >
                      Excel татах
                    </Button>
                    <span>
                      Нийт дүн:{" "}
                      <strong>
                        {(holdersData?.totalAmount ?? 0).toLocaleString()}₮
                      </strong>
                    </span>
                  </div>
                  <Table
                    scroll={{ x: 900 }}
                    dataSource={holdersData?.holders ?? []}
                    rowKey="historyId"
                    size="small"
                    loading={isHoldersLoading}
                    pagination={{
                      pageSize: limit,
                      total: holdersData?.totalCount ?? 0,
                    }}
                    onChange={(pagination) =>
                      setHoldersPage(Number(pagination?.current) - 1)
                    }
                    bordered
                    columns={[
                      {
                        title: "Овог",
                        dataIndex: "lastName",
                        key: "lastName",
                      },
                      {
                        title: "Нэр",
                        dataIndex: "firstName",
                        key: "firstName",
                      },
                      {
                        title: "Утас",
                        dataIndex: "phoneNo",
                        key: "phoneNo",
                      },
                      {
                        title: "Дүн",
                        dataIndex: "principleAmount",
                        key: "principleAmount",
                        render: (val: number) => val?.toLocaleString(),
                      },
                      {
                        title: "Худалдан авсан огноо",
                        dataIndex: "purchaseDate",
                        key: "purchaseDate",
                        render: (val: number) =>
                          val ? moment(val).format("YYYY-MM-DD") : "-",
                      },
                      {
                        title: "Сүүлийн хүү олголт",
                        dataIndex: "lastPayoutAt",
                        key: "lastPayoutAt",
                        render: (val: number) =>
                          val ? moment(val).format("YYYY-MM-DD") : "-",
                      },
                    ]}
                  />
                </>
              ),
            },
            {
              key: "payouts",
              label: "Хүү тараалт",
              children: (
                <Table
                  scroll={{ x: 900 }}
                  dataSource={payoutsData?.payouts ?? []}
                  rowKey={(record) =>
                    `${record.userId}-${record.batchId}-${record.createdAt}`
                  }
                  size="small"
                  loading={isPayoutsLoading}
                  pagination={{
                    pageSize: limit,
                    total: payoutsData?.totalCount ?? 0,
                  }}
                  onChange={(pagination) =>
                    setPayoutsPage(Number(pagination?.current) - 1)
                  }
                  bordered
                  columns={[
                    {
                      title: "Овог",
                      dataIndex: "lastName",
                      key: "lastName",
                    },
                    {
                      title: "Нэр",
                      dataIndex: "firstName",
                      key: "firstName",
                    },
                    {
                      title: "Batch",
                      dataIndex: "batchId",
                      key: "batchId",
                    },
                    {
                      title: "Үндсэн дүн",
                      dataIndex: "principleAmount",
                      key: "principleAmount",
                      render: (val: number) => val?.toLocaleString(),
                    },
                    {
                      title: "Хүү",
                      dataIndex: "interest",
                      key: "interest",
                      render: (val: number) => val?.toLocaleString(),
                    },
                    {
                      title: "Татвар",
                      dataIndex: "taxAmount",
                      key: "taxAmount",
                      render: (val: number) => val?.toLocaleString(),
                    },
                    {
                      title: "Огноо",
                      dataIndex: "createdAt",
                      key: "createdAt",
                      render: (val: number) =>
                        val ? moment(val).format("YYYY-MM-DD") : "-",
                    },
                  ]}
                />
              ),
            },
            {
              key: "withdrawals",
              label: "Буцаалтууд",
              children: (
                <Table
                  scroll={{ x: 900 }}
                  dataSource={withdrawalsData?.withdrawals ?? []}
                  rowKey={(record) => `${record.userId}-${record.createdAt}`}
                  size="small"
                  loading={isWithdrawalsLoading}
                  pagination={{
                    pageSize: limit,
                    total: withdrawalsData?.totalCount ?? 0,
                  }}
                  onChange={(pagination) =>
                    setWithdrawalsPage(Number(pagination?.current) - 1)
                  }
                  bordered
                  columns={[
                    {
                      title: "Овог",
                      dataIndex: "lastName",
                      key: "lastName",
                    },
                    {
                      title: "Нэр",
                      dataIndex: "firstName",
                      key: "firstName",
                    },
                    {
                      title: "Дүн",
                      dataIndex: "amount",
                      key: "amount",
                      render: (val: number) => val?.toLocaleString(),
                    },
                    {
                      title: "Буцаалтын дүн",
                      dataIndex: "withdrawnAmount",
                      key: "withdrawnAmount",
                      render: (val: number) =>
                        val != null ? val.toLocaleString() : "-",
                    },
                    {
                      title: "Статус",
                      dataIndex: "status",
                      key: "status",
                      render: (val: string) => <Tag>{val}</Tag>,
                    },
                    {
                      title: "Худалдан авсан",
                      dataIndex: "purchasedAt",
                      key: "purchasedAt",
                      render: (val: number) =>
                        val ? moment(val).format("YYYY-MM-DD") : "-",
                    },
                    {
                      title: "Огноо",
                      dataIndex: "createdAt",
                      key: "createdAt",
                      render: (val: number) =>
                        val ? moment(val).format("YYYY-MM-DD") : "-",
                    },
                  ]}
                />
              ),
            },
            {
              key: "batches",
              label: "Хүү олголтын явц",
              children: (
                <Table
                  scroll={{ x: 900 }}
                  dataSource={batchesData ?? []}
                  rowKey="_id"
                  size="small"
                  loading={isBatchesLoading}
                  pagination={false}
                  bordered
                  columns={[
                    {
                      title: "Огноо",
                      dataIndex: "batchId",
                      key: "batchId",
                      render: (val: string) =>
                        val
                          ? `${val.slice(0, 4)}-${val.slice(4, 6)}-${val.slice(6, 8)}`
                          : "-",
                    },
                    {
                      title: "Төлөв",
                      dataIndex: "status",
                      key: "status",
                      render: (val: string) => {
                        const info = STATUS_LABELS[val] ?? {
                          text: val,
                          color: "default",
                        };
                        return <Tag color={info.color}>{info.text}</Tag>;
                      },
                    },
                    {
                      title: "Явц",
                      key: "progress",
                      render: (_: unknown, record) => {
                        const total = record.totalTasks ?? 0;
                        const done =
                          (record.completedTasks ?? 0) +
                          (record.failedTasks ?? 0);
                        const percent =
                          total === 0 ? 100 : Math.floor((done / total) * 100);
                        return (
                          <Progress
                            percent={percent}
                            size="small"
                            status={
                              record.status === "failed"
                                ? "exception"
                                : record.status === "completed"
                                  ? "success"
                                  : "active"
                            }
                          />
                        );
                      },
                    },
                    {
                      title: "Нийт",
                      dataIndex: "totalTasks",
                      key: "totalTasks",
                    },
                    {
                      title: "Биелсэн",
                      dataIndex: "completedTasks",
                      key: "completedTasks",
                    },
                    {
                      title: "Алдаатай",
                      dataIndex: "failedTasks",
                      key: "failedTasks",
                      render: (val: number) =>
                        val > 0 ? (
                          <span style={{ color: "#cf1322" }}>{val}</span>
                        ) : (
                          val
                        ),
                    },
                    {
                      title: "Эхэлсэн",
                      dataIndex: "startedAt",
                      key: "startedAt",
                      render: (val: number) =>
                        val ? moment(val).format("YYYY-MM-DD HH:mm") : "-",
                    },
                    {
                      title: "Дууссан",
                      dataIndex: "completedAt",
                      key: "completedAt",
                      render: (val: number) =>
                        val ? moment(val).format("YYYY-MM-DD HH:mm") : "-",
                    },
                  ]}
                />
              ),
            },
          ]}
        />
      </div>
    </>
  );
};
